import React, { useEffect, useState } from 'react';
import {
  useIsAuthenticated,
  useMsal,
  MsalProvider,
} from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { msalInstance, loginRequest } from './msalConfig';
import { AuthUserContext } from './AuthUserContext';
import { getUserInfo, UserInfo } from './getUserInfo';
import { Button } from 'antd';

type AuthGateProps = {
  children?: React.ReactNode;
};

/**
 * Wraps children with MsalProvider and gates rendering on authentication.
 */
export const AuthGate = ({ children }: AuthGateProps) => {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthGateInner>{children}</AuthGateInner>
    </MsalProvider>
  );
};

const AuthGateInner = ({ children }: { children?: React.ReactNode }) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      getUserInfo()
        .then((info) => {
          setUserInfo(info);
        })
        .catch((err) => {
          console.error('[AuthGate] Failed to get user info', err);
          setError(true);
        });
    }
  }, [isAuthenticated]);

  if (error) {
    return <StatusMessage>Authentication failed.</StatusMessage>;
  }

  if (inProgress !== InteractionStatus.None) {
    return <StatusMessage>Authentication in progress...</StatusMessage>;
  }

  if (!isAuthenticated) {
    return <UnauthenticatedView />;
  }

  if (userInfo === null) {
    return <StatusMessage>Checking access...</StatusMessage>;
  }

  return (
    <AuthUserContext.Provider value={{ userInfo }}>
      {children}
    </AuthUserContext.Provider>
  );
};

const UnauthenticatedView = () => {
  const { instance } = useMsal();

  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch((error) => {
      console.error('Login failed', error);
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      <span
        style={{ fontSize: '16px', color: '#ffff', marginBottom: '20px' }}
      >
        There are currently no users signed in!
      </span>
      <Button type="primary" onClick={handleLogin}>
        Login
      </Button>
    </div>
  );
};

const StatusMessage = ({ children }: { children: React.ReactNode }) => {
  return <div style={{ textAlign: 'center', padding: 20 , color: '#ffff' }}>{children}</div>;
};
